import moment from 'moment';
import _sortBy from 'lodash/sortBy';
import _cloneDeep from 'lodash/cloneDeep';
import Config from 'services/config/local';

/**
 * Utils class, helper functions
 */
export default class Utils {
    /**
     * Convert timestamp to given format
     * @param {int} timestamp - Integer representation of unix timestamp, in nanoseconds
     * @param {string} format - DateTime format
     * @returns {string} timestamp converted into string datetime representation
     * @static
     */
    static convertTimestampToFormat(timestamp, format) {
        const unixTime = parseInt(timestamp / Config.nanoMultiplier, 10);
        return moment(unixTime).format(format);
    }

    /**
     * Replace %key% to value from parameters in given string value
     * @param {Object} parameters - Object with key:value
     * @param {string} value - String value for processing
     * @returns {string} parsed value
     * @static
     */
    static formatString(parameters, value) {
        let result = value;

        Object.keys(parameters).forEach((parameter) => {
            result = result.split(`%${parameter}%`).join(parameters[parameter]);
        });

        return result;
    }

    /**
     * Crawl given obj by given path and return last item from the path
     * @param {Object} obj - Object for crawl
     * @param {string} path - Path with "." delimiter
     * @param {boolean} deleteLast - if True - delete last found object
     * @returns {boolean|object} if deleteLast is given than return is true otherway return is last found object
     * @static
     */
    static crawlObjectByPath(obj, path, deleteLast) {
        const pathParts = path.split('.');
        const pathLength = pathParts.length;
        const lastStep = pathLength - 1;
        let currentObject = obj;

        for (let i = 0; i < pathLength; i++) {
            if (deleteLast && i === lastStep) {
                delete currentObject[pathParts[i]];

                return true;
            }

            currentObject = currentObject[pathParts[i]];
        }

        return currentObject;
    }

    /**
     * Create structure in obj from given path and assign value to last item
     * @param {Object} obj - Original object
     * @param {string} path - Path with "." delimiter
     * @param {*} value - Value to assign to last item in path created in obj
     * @returns {Object} Last object created in obj from path
     * @static
     */
    static createPath(obj, path, value) {
        const pathParts = path.split('.');
        const pathLength = pathParts.length;
        const lastStep = pathLength - 1;
        let currentObject = obj;

        for (let i = 0; i < pathLength; i++) {
            const step = pathParts[i];

            if (!(step in currentObject)) {
                if (value && i === lastStep) {
                    currentObject[step] = value;

                    return currentObject;
                }

                currentObject[step] = {};
            } else if (value && i === lastStep) {
                currentObject[step] = value;

                return currentObject;
            }

            currentObject = currentObject[step];
        }

        return currentObject;
    }

    /**
     * Sort unsorted array by given key
     * @param {Array} unsorted - Array of object items
     * @param {string} key - Name of key for sort operation
     * @returns {Array} sorted array or empty array if unsorted is not an array or has no items or undefined
     * @static
     */
    static sortItemsByKey(unsorted, key) {
        if (unsorted && Array.isArray(unsorted) && unsorted.length) {
            return _sortBy(unsorted, key);
        }

        return [];
    }

    /**
     * Remove keys from obj
     * @param {Object} obj
     * @param {Array} keys - Array of key names
     * @returns {Object} If keys is empty original obj is returned, otherwise new object with removed key returned
     * @static
     */
    static removeKeysFromObject(obj, keys) {
        if (!keys.length) return obj;

        const tempObj = _cloneDeep(obj);

        keys.forEach((key) => {
            if (key in tempObj) {
                delete tempObj[key];
            }
        });

        return tempObj;
    }

    /**
     * Parse response object for Sensors and Connectors tabs component and fix difference in naming
     * @param {Object} params - Object with function parameters
     * @param {{objects: Array<Object>}} params.data - Response from server
     * @param {Array<string>} [params.removeKeys=[]] - optional, List of key names to be removed from response
     * @param {string} [params.groupType='CATEGORY'] - optional, used to determine if item is category/group
     * @param {boolean} [params.addEmptyItemsArrayOnGroup=false] - optional,
     * if True - will add empty items:[] to category/group
     * @returns {Array} New array of fixed objects
     * @static
     */
    static parseNavigationData({ data, removeKeys = [], groupType = 'CATEGORY', addEmptyItemsArrayOnGroup = false }) {
        if (!data || !data.objects || !Array.isArray(data.objects) || !data.objects.length) return [];

        const addName = (item) => {
            if (!('title' in item) && 'name' in item) {
                item.title = item.name;
            }
        };
        const parseItem = (child) => {
            addName(child);

            return Utils.removeKeysFromObject(child, removeKeys);
        };
        const initializeItemsArray = (obj) => {
            if (groupType === obj.type && addEmptyItemsArrayOnGroup && !('items' in obj)) {
                obj.items = [];
            }
        };
        const items = [];

        data.objects.forEach((item) => {
            addName(item);

            if (item.children && Array.isArray(item.children) && item.children.length) {
                item.items = item.children.map(parseItem);

                delete item.children;
            } else if (item.items && Array.isArray(item.items) && item.items.length) {
                item.items = item.items.map(parseItem);
            }

            initializeItemsArray(item);

            items.push(Utils.removeKeysFromObject(item, removeKeys));
        });

        return items;
    }

    static buildApiUrl(key, params = {}) {
        const [section, type] = key.split('.');
        const url = `${Config.api.host}${Config.api.version}${Config.api[section][type]}`;
        return Utils.formatString(params, url);
    }

    static compareByKey(key) {
        return (a, b) => {
            if (a[key] < b[key]) {
                return -1;
            }
            if (a[key] > b[key]) {
                return 1;
            }
            return 0;
        };
    }

    static buildSearchIndex(data, config = {}) {
        const index = [];
        const idKey = config.id || 'id';
        const displayNameKey = config.displayName || 'name';
        data.forEach((item) => {
            index.push({
                id: item[idKey],
                name: item[displayNameKey],
                searchableName: item[displayNameKey].toLowerCase()
            });
        });
        return index.sort(Utils.compareByKey('searchableName'));
    }

    static searchInIndex(index, query, provideEverything = false) {
        const searchableQuery = query.toLowerCase();

        if (!query && provideEverything) {
            return index;
        }

        return index.filter(item => item.searchableName.indexOf(searchableQuery) >= 0);
    }

    static intervalToText(seconds) {
        const minutes = seconds / 60;
        if (minutes < 5) {
            return '5m';
        }
        if (minutes < 60) {
            return `${Math.round(minutes)}m`;
        }
        const hours = minutes / 60;
        if (hours < 24) {
            return `${Math.round(hours)}h`;
        }
        const days = hours / 24;
        if (days < 7) {
            return `${Math.round(days)}d`;
        }
        const weeks = days / 7;
        return `${Math.round(weeks)}w`;
    }

    static httpExtractData(response) {
        return response.data;
    }

    static noop() {}
}



// WEBPACK FOOTER //
// ./app/services/utils/index.js